import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/**
 * NewsletterForm — captures an email into Resend Audience via
 * POST /api/newsletter/subscribe. Falls back to DB-only when Resend not set.
 *
 * Props:
 *  - source: string tag ("home" | "insights") for analytics
 *  - variant: "card" (default, full block) | "inline" (compact one-liner)
 *  - eyebrow: optional overline
 *  - title / body: optional overrides
 */
export default function NewsletterForm({
  source = "site",
  variant = "card",
  eyebrow = "// Newsletter",
  title = "Get weekly Corporate Athlete 2.0 insights.",
  body = "One short essay a week — leadership, AI, and the compounding habits that outrun burnout. No spam. Unsubscribe anytime.",
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const isInline = variant === "inline";

  const onSubmit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", {
        email: clean,
        first_name: firstName.trim() || null,
        source,
      });
      setSubscribed(true);
      setEmail("");
      setFirstName("");
      toast.success("You're in", {
        description: "Check your inbox — the next essay drops on Sunday.",
      });
    } catch (err) {
      toast.error("Subscription failed", {
        description:
          err?.response?.data?.detail ||
          "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isInline) {
    return (
      <form
        onSubmit={onSubmit}
        data-testid={`newsletter-form-${source}`}
        className="flex flex-col sm:flex-row gap-2 w-full max-w-lg"
        noValidate
      >
        <Input
          type="email"
          data-testid={`newsletter-email-input-${source}`}
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-sm flex-1"
          aria-label="Email address"
          disabled={loading || subscribed}
        />
        <Button
          type="submit"
          data-testid={`newsletter-submit-button-${source}`}
          disabled={loading || subscribed}
          className="rounded-sm gap-2 group"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : subscribed ? (
            <>Subscribed</>
          ) : (
            <>
              Subscribe
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <div
      data-testid={`newsletter-card-${source}`}
      className="relative border border-border bg-background overflow-hidden"
    >
      <div className="hairline-ai" />
      <div className="p-6 sm:p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" />
            {eyebrow}
          </p>
          <h3 className="mt-3 font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight max-w-xl">
            {title}
          </h3>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-xl">
            {body}
          </p>
        </div>

        <div className="lg:col-span-5">
          {subscribed ? (
            <div
              data-testid={`newsletter-success-${source}`}
              className="flex items-start gap-3 border border-border p-4 bg-secondary/50"
            >
              <div className="h-9 w-9 grid place-items-center border border-border bg-background">
                <Mail className="h-4 w-4 text-accent" />
              </div>
              <div>
                <p className="text-sm font-medium">You're on the list.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Next essay lands Sunday morning.
                </p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid={`newsletter-form-${source}`}
              className="space-y-3"
              noValidate
            >
              <Input
                data-testid={`newsletter-firstname-input-${source}`}
                placeholder="First name (optional)"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="rounded-sm"
                aria-label="First name"
              />
              <Input
                type="email"
                data-testid={`newsletter-email-input-${source}`}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm"
                aria-label="Email address"
                required
              />
              <Button
                type="submit"
                data-testid={`newsletter-submit-button-${source}`}
                disabled={loading}
                size="lg"
                className="w-full rounded-sm gap-2 group"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Subscribing…
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
              <p className="text-[11px] font-mono-ui text-muted-foreground text-center">
                No spam. Unsubscribe in one click.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
